import { Component, EventEmitter, inject, Input, OnChanges, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { StructureInstance, StructureStatus, StructureBaseModel, StructureFilter } from "@models/structure.model";
import { GenericDialogComponent } from "../../shared/generic-dialog/generic-dialog.component";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { StructureService } from "../../services/structure/structure.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { TranslateService } from "@ngx-translate/core";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { DialogService } from "../../services/dialog/dialog.service";

@Component({
    selector: "app-my-structures-list",
    templateUrl: "./my-structures-list.component.html",
    styleUrls: ["./my-structures-list.component.scss"],
    standalone: false,
})
export class MyStructuresListComponent implements OnChanges {
    @Input() structures: { totalCount: number; count: number; rows: StructureInstance[]; status: string } = {
        totalCount: 0,
        count: 0,
        rows: [],
        status: StructureFilter.All,
    };
    @Input() selectedStructureIds = new Set<number>();
    @Input() loadingStructure: boolean = false;
    @Output() loadMore = new EventEmitter<Boolean>();
    @Output() structureUpdated = new EventEmitter<StructureInstance>();
    @Output() structureDeleted = new EventEmitter<Set<number>>();
    @Output() checkboxToggled = new EventEmitter<{ id: number; checked: boolean }>();

    public savedStructuresCount = 0;
    public showViewMoreButton: boolean = false;
    public selectedCheckboxes = 0;
    public structureStatus = StructureStatus;
    private dialog = inject(MatDialog);
    public structureFilter = StructureFilter;

    constructor(
        private structureService: StructureService,
        private snackBar: MatSnackBar,
        private router: Router,
        private translate: TranslateService,
        private dialogService: DialogService,
    ) {}

    ngOnChanges(): void {
        this.showViewMoreButton = this.structures.count > this.structures.rows.length;
        if (this.structures?.rows) {
            this.savedStructuresCount = this.structures.rows.filter(
                (structure) => structure.status === this.structureStatus.Saved,
            ).length;
        }
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    public onViewMore(): void {
        this.loadMore.emit();
    }

    public onPublish(structureId: number, structureStatus: string): void {
        if (structureStatus !== StructureStatus.Saved) {
            return;
        }
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.MyStructuresList,
                title: this.getTranslation("genericDialogBox.publishStructure.title"),
                content: this.getTranslation("genericDialogBox.publishStructure.content"),
                firstBtn: this.getTranslation("genericDialogBox.button.publishBtn"),
                secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.structureService.publishStructures([structureId]).subscribe({
                    next: (response) => {
                        const existing = this.structures.rows.find((struct) => struct.id === structureId);
                        if (!existing) return;

                        const updatedStructure: StructureInstance = {
                            ...existing,
                            status: StructureStatus.Published,
                        };
                        this.selectedStructureIds.delete(structureId); // Remove deleted structure from the current selection
                        this.structureUpdated.emit(updatedStructure);

                        this.snackBar.open(response.message, SnackBarConfig.Actions.CLOSE, {
                            duration: SnackBarConfig.Duration.MEDIUM,
                        });
                    },
                    error: (error) => {
                        this.snackBar.open(error, SnackBarConfig.Actions.CLOSE, {
                            duration: SnackBarConfig.Duration.MEDIUM,
                        });
                    },
                });
            }
        });
    }

    public onDelete(structureId: number, structureStatus: string): void {
        if (structureStatus !== StructureStatus.Saved) {
            return;
        }
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.MyStructuresList,
                title: this.getTranslation("genericDialogBox.deleteStructure.title"),
                content: this.getTranslation("genericDialogBox.deleteStructure.content"),
                firstBtn: this.getTranslation("genericDialogBox.button.okBtn"),
                secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.structureService.deleteStructures([structureId]).subscribe({
                    next: (response) => {
                        this.selectedStructureIds.delete(structureId); // Remove deleted structure from the current selection
                        this.structureDeleted.emit(new Set([structureId]));
                        this.snackBar.open(response.message, SnackBarConfig.Actions.CLOSE, {
                            duration: SnackBarConfig.Duration.MEDIUM,
                        });
                    },
                    error: (error) => {
                        console.log(error);
                        this.snackBar.open(error, SnackBarConfig.Actions.CLOSE, {
                            duration: SnackBarConfig.Duration.MEDIUM,
                        });
                    },
                });
            }
        });
    }

    public isStructureSelected(structureId: number): boolean {
        return this.selectedStructureIds.has(structureId); // Returns true if the structure is currently selected.
    }

    //properties
    public openPropertiesDialog(userStructureData: StructureInstance): void {
        const dialogData = {
            componentName: DialogInvokingComponents.MyStructuresListProperties,
            title: this.getTranslation("genericDialogBox.structureProperties.title"),
            structureName: userStructureData.structureName!.trim(),
            structureDescription: userStructureData.structureDescription!.trim(),
            status: userStructureData.status,
            firstBtn: this.getTranslation("genericDialogBox.button.saveBtn"),
            secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
        };

        this.dialogService
            .openDialog(dialogData, "90%", { disableClose: true }, "550px")
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    const structureData: StructureBaseModel = {
                        structureName: result.structureName.trim(),
                        structureDescription: result.structureDescription.trim(),
                        status: result.status,
                    };

                    this.structureService.updateStructure(+userStructureData?.id!, structureData).subscribe({
                        next: (response) => {
                            this.structureUpdated.emit({
                                ...userStructureData,
                                ...structureData,
                            });
                            this.snackBar.open(response.message, SnackBarConfig.Actions.OKAY, {
                                duration: SnackBarConfig.Duration.MEDIUM,
                            });
                        },
                        error: (error) => {
                            this.snackBar.open(error.error, SnackBarConfig.Actions.OKAY, {
                                duration: SnackBarConfig.Duration.MEDIUM,
                            });
                        },
                    });
                }
            });
    }

    public onCheckboxChange(event: MatCheckboxChange, structureId: number): void {
        this.checkboxToggled.emit({
            id: structureId,
            checked: event.checked,
        });
    }

    public goToMyStuctureDesign(myStructureId: number): void {
        const structureDetails = this.structures.rows.find((structure) => structure.id === myStructureId);

        if (!structureDetails) {
            this.snackBar.open("Structure not found", SnackBarConfig.Actions.OKAY, {
                duration: SnackBarConfig.Duration.MEDIUM,
            });
            return;
        }

        this.router.navigate([`designer/${myStructureId}`]);
    }

    public goTodesigner() {
        this.router.navigate(["/designer"]);
    }

    public goToWishlist() {
        this.router.navigate(["/wishlist"]);
    }

    public goToBestSeller() {
        this.router.navigate(["/products"], { queryParams: { category: "bestSeller" } });
    }

    public goToFeature() {
        this.router.navigate(["/products"], { queryParams: { category: "featured" } });
    }
}
